import type { AnalyticsPeriod } from '../types/analytics';
import { apiRequest } from './apiClient';

export type ApiTransaction = {
  id: string;
  date: string;
  amount: number;
  direction: 'debit' | 'credit';
  kind: string;
  tab: 'spend' | 'wealth';
  merchant: string;
  merchantNormalized: string;
  description?: string;
  categoryId?: string;
  categoryName?: string;
  source?: string;
  importBatchId?: string;
  spendAccountId?: string;
  /** Uploader — used for owner attribution + edit gating. */
  userId?: string;
  /** When true, excluded from partners' combined household view. */
  hiddenFromHousehold?: boolean;
};

export type PatchTransactionBody = {
  categorySlug?: string;
  categoryName?: string;
  hiddenFromHousehold?: boolean;
};

export type ListTransactionsResponse = {
  period?: AnalyticsPeriod | null;
  periodKey?: string | null;
  date?: string | null;
  importBatchId?: string | null;
  transactions: ApiTransaction[];
  nextCursor?: string | null;
  hasMore?: boolean;
};

export type ListTransactionsOptions = {
  accessToken: string;
  tab?: 'spend' | 'wealth';
  period?: AnalyticsPeriod;
  periodKey?: string;
  /** Single UTC day `YYYY-MM-DD` — takes precedence over period. */
  date?: string;
  categorySlug?: string;
  merchantNormalized?: string;
  importBatchId?: string;
  spendAccountId?: string;
  /** Mine vs Household combined view. */
  view?: 'personal' | 'combined';
  limit?: number;
  cursor?: string | null;
};

export async function listTransactions(
  options: ListTransactionsOptions,
): Promise<ListTransactionsResponse> {
  const params = new URLSearchParams();
  if (options.importBatchId) {
    params.set('importBatchId', options.importBatchId);
  } else {
    params.set('tab', options.tab ?? 'spend');
    if (options.date) {
      params.set('date', options.date);
    } else {
      params.set('period', options.period ?? 'month');
      if (options.periodKey) params.set('periodKey', options.periodKey);
    }
    if (options.categorySlug) params.set('categorySlug', options.categorySlug);
    if (options.merchantNormalized) {
      params.set('merchantNormalized', options.merchantNormalized);
    }
    if (options.spendAccountId) params.set('spendAccountId', options.spendAccountId);
    if (options.view) params.set('view', options.view);
  }
  if (options.limit != null) params.set('limit', String(options.limit));
  if (options.cursor) params.set('cursor', options.cursor);

  return apiRequest<ListTransactionsResponse>(`/transactions?${params.toString()}`, {
    headers: { Authorization: `Bearer ${options.accessToken}` },
  });
}

export async function patchTransaction(
  id: string,
  body: PatchTransactionBody,
): Promise<{ transaction: ApiTransaction }> {
  return apiRequest<{ transaction: ApiTransaction }>(`/transactions/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}
